import { format } from "date-fns";
import { ReactComponent as MoodStreakHeartIcon } from "shared/assets/Icons/mood-streak-heart.svg";
import { Mood, moodTickIcon } from "UIPalette/MoodTrackerCard/moodFactories";
import { MoodTrackerCard, MoodTrackerWellbeingId } from "UIPalette/MoodTrackerCard/MoodTrackerCard";
import classNames from "classnames";
import { TodaysMoodCard } from "../TodaysMoodCard/TodaysMoodCard";
import { RoundedCard } from "shared/UI/RoundedCard/RoundedCard";
import { formatDate, getCurrentWeekDates } from "core/helpers/dates";
import { ViewStateWrapper } from "UIPalette/ViewStateWrapper/ViewStateWrapper";
import { StreakStatusModal } from "./StreakStatusModal";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStatusModal } from "shared/core/hooks/useStatusModal";
import { useEffect } from "react";
import { useMoodTrackerContext } from "core/state/Trackers/MoodTrackerContext";
import MoodSupportModal from "UIPalette/MoodSupportModal/MoodSupportModal";
import { useAnimation } from "../../../../hooks/useAnimation/useAnimation";
import { Animations } from "../../../../hooks/useAnimation/animations.enum";
import { playHaptic } from "core/javascriptChannels";
import CardNavButton from "UIPalette/CardNavButton/CardNavButton";
import { MoodJourneyModalTypes, MoodJourneyPageModal } from "../MoodJourneyPageModal/MoodPageModal";

export const MoodTrackerModule = ({ hideYourInsights }: { hideYourInsights?: boolean }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    moodStats: weekMoodStats,
    moodAction,
    streakData,
    currentMood,
    hasError,
    isLoading,
    setCurrentMoodValue,
    clearCurrentMoodValue,
    refreshMoodValues,
    showStreakModal,
    handleCloseStreakModal,
    showMoodJourneyModal,
    handleCloseMoodJourneyModal,
    errorDisplay,
    handleErrorDisplay,
  } = useMoodTrackerContext();
  const { Modal, setBody } = useStatusModal();
  const { trigger: animateHeartbeat, targetRef: heartRef, isAnimating: isAnimatingHeartbeat } = useAnimation(Animations.Heartbeat);

  const runStreakFeedback = () => {
    if (heartRef.current && !isAnimatingHeartbeat.current) {
      playHaptic();
      animateHeartbeat();
    }
  };

  const handleSetMood = (value: Mood) => {
    setCurrentMoodValue(value);
    runStreakFeedback();
  };

  useEffect(() => {
    if (errorDisplay !== null) {
      setBody({
        data: {
          title: t("generalErrors.title"),
          description: errorDisplay,
          cta: t("generalErrors.cta"),
        },
        status: "error",
        callback: () => {
          setBody(null);
          handleErrorDisplay(null);
        },
      });
    }
  }, [errorDisplay, setBody, handleErrorDisplay, t]);

  const { dates } = getCurrentWeekDates();

  const hasCurrentMood = currentMood && currentMood > 0;
  return (
    <>
      <ViewStateWrapper loading={isLoading && !hasError} error={hasError && !currentMood} onRetry={refreshMoodValues}>
        <RoundedCard id={MoodTrackerWellbeingId} className="space-y-2 min-h-[363px]  overflow-hidden" padding="0">
          <div className="p-5">
            {hasCurrentMood ? (
              <TodaysMoodCard currentMood={currentMood} clearCurrentMoodValue={clearCurrentMoodValue} />
            ) : (
              <MoodTrackerCard setCurrentMoodValue={handleSetMood} />
            )}
          </div>
          <hr className="border-t-border-grey" />
          <div className="p-5 space-y-6">
            <div className="flex items-center space-x-3">
              <div ref={heartRef}>
                <MoodStreakHeartIcon />
              </div>
              <div>
                <p className="font-bold text-stat">{streakData?.days ?? 0}</p>
                <p>{t("mood-insights.streak-text")}</p>
              </div>
            </div>
            <div className="w-full flex justify-between items-center">
              {dates.map((date) => {
                const today = new Date();
                const dateValue = new Date(date);
                const isToday = formatDate(dateValue) === formatDate(today);
                const mood = weekMoodStats?.moods.find((data) => formatDate(new Date(data.createdDateUtc)) === formatDate(dateValue))?.mood;
                const dayValue = format(dateValue, "EEEE")[0];

                return (
                  <div
                    key={date}
                    className={classNames("flex flex-col items-center gap-2", {
                      "bg-brand-grey-light p-2 rounded-full": isToday,
                    })}
                  >
                    {moodTickIcon(mood)}
                    <p className={classNames({ "font-bold": isToday })}>{dayValue}</p>
                  </div>
                );
              })}
            </div>
          </div>
          {!hideYourInsights && (
            <CardNavButton aria-label={t("mood-insights.cta")} label={t("mood-insights.cta")} onClick={() => navigate(`/mood-insights`)} />
          )}
        </RoundedCard>
        {Modal}
        <StreakStatusModal event={streakData?.status} visible={showStreakModal} handleClose={handleCloseStreakModal} />
        {moodAction && showMoodJourneyModal && (
          <MoodJourneyPageModal mood={moodAction as MoodJourneyModalTypes} visible={showMoodJourneyModal} handleClose={handleCloseMoodJourneyModal} />
        )}
      </ViewStateWrapper>
      <MoodSupportModal />
    </>
  );
};
